import {determineLife} from 'services/personFunctions';
import Api, {dateToServer} from 'services/Api';
import moment from 'moment';

const mutationTypes = {
    UPDATE_VALUE: 'UPDATE_VALUE',
    UPDATE_YEARS_AND_MONTHS: 'UPDATE_YEARS_AND_MONTHS',
    UPDATE_LIFE_EXPECTANCY: 'UPDATE_LIFE_EXPECTANCY',
    RESET: 'RESET',
    RESET_END_OF_LIFE: 'RESET_END_OF_LIFE',
    UPDATE_RESULTS: 'UPDATE_RESULTS',
    UPDATE_ASSUMED_SETTLEMENT: 'UPDATE_ASSUMED_SETTLEMENT'
};

// Initial State
const state = {
    person: {
        gender: '',
        dateOfBirth: null,
        method: '',
        yearsAndMonths: {
            months: 0,
            years: 0,
        },
        dateOfDeathIsAssumed: null,
        ageAtDeath: null,
        dateOfDeath: null,
        percentageOfNormalLifeExpectancy: 100,
        percentageReductionInNormalLifeExpectancy: 0,
    },
    assumedSettlement: null,
    results: {
        formula: null,
        tableData: null,
        intro: null,
        explanation: null,
        outcome: null,
    }
};

/**
 * Server Request Ready?
 * 
 * @param  {object} lifeExpectancy
 * @param {function} dispatch
 * 
 * @return {boolean}               
 */
const isValid = (lifeExpectancy, dispatch) => {
    if (lifeExpectancy.method !== 'age_at_death') {
        dispatch('resetEndOfLife');
    }

    return lifeExpectancy.method !== ''
        && !!lifeExpectancy.gender
        && !!lifeExpectancy.dateOfBirth;
};

/**
 * Cycle over an object and dispatch an update for each key.
 *
 * @param {function} dispatch
 * @param {string} action
 * @param {{}} obj
 * @param {number|null} [index]
 *
 * @return {Promise}
 */
const updateObject = (dispatch, action, obj, index = null) => {
    let proms = Object.keys(obj).map(key => dispatch(action, {key, value: obj[key], index}));
    return Promise.all(proms);
};

/**
 * Throttle update of person life expectancy to allow for typing
 */
export const throttleUpdateLife = _.debounce((dispatch, action, person, lifeExpectancy, assumedSettlement, index) => {
    determineLife(assumedSettlement, person, lifeExpectancy)
        .then(updatedLife => updateObject(dispatch, action, updatedLife, index));
}, 500);

/**
 * Prepare post date
 *
 * @param state
 * @param {string} value
 *
 * @return {object}
 */
const postData = (state, value) => {
    return {
        person: {
            name: 'mini-calc',
            gender: state.person.gender,
            date_of_birth: dateToServer(state.person.dateOfBirth),
        },
        claim: {
            assumed_settlement: moment().format('YYYY-MM-DD'),
        },
        type: 'person',
        value
    };
};

/**
 * Determine a users life expectancy from life expectancy options
 *
 * @param state
 */
const getLifeData = (state) => {
    switch (state.person.method) {
    case 'ogden_table':
        return Api.miniCalculators.lifeExpectancy.ogden(postData(state));
    case 'percentage_of_normal_life_expectancy':
        return Api.miniCalculators.lifeExpectancy.percentageOfNormal(postData(state, state.person.percentageOfNormalLifeExpectancy));
    case 'percentage_reduction_in_normal_life_expectancy':
        return Api.miniCalculators.lifeExpectancy.percentageReduction(postData(state, state.person.percentageReductionInNormalLifeExpectancy));
    }
};

// Getters
const getters = {
    thePerson: (state) => state.person,
    theResults: (state) => state.results,
    getAssumedSettlement: (state) => state.assumedSettlement
};

// Actions
const actions = {
    updatePerson({commit}, {key, value}) {
        commit(mutationTypes.UPDATE_VALUE, {key, value});
    },
    updateYearsAndMonths({commit}, {key, value}) {
        commit(mutationTypes.UPDATE_YEARS_AND_MONTHS, {key, value});
    },
    updateAssumedSettlement({commit}, value) {
        commit(mutationTypes.UPDATE_ASSUMED_SETTLEMENT, value);
    },
    reset({commit}) {
        commit(mutationTypes.RESET);
    },
    updateLifeExpectancy({dispatch, state}) {
        if (!isValid(state.person, dispatch)) { return;}

        if (state.person.method === 'ogden_table'
            || state.person.method === 'percentage_of_normal_life_expectancy'
            || state.person.method === 'percentage_reduction_in_normal_life_expectancy') {
            getLifeData(state).then((data) => dispatch('updateResults', data));
        }

        return updateObject(dispatch, 'updatePerson', state.person)
            .then(() => throttleUpdateLife(dispatch, 'updatePerson', state.person, state.person, state.assumedSettlement));
    },
    resetEndOfLife({commit}) {
        commit(mutationTypes.RESET_END_OF_LIFE);
    },
    updateResults({commit}, data) {
        commit(mutationTypes.UPDATE_RESULTS, data);
    },
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE_VALUE](state, payload) {
        state.person[payload.key] = payload.value;
    },
    [mutationTypes.UPDATE_ASSUMED_SETTLEMENT](state, value) {
        state.assumedSettlement = value;
    },
    [mutationTypes.UPDATE_YEARS_AND_MONTHS](state, payload) {
        state.person.yearsAndMonths[payload.key] = payload.value;
    },
    [mutationTypes.RESET](state) {
        state.person.gender = '';
        state.person.dateOfDeath = null;
        state.person.dateOfBirth = null;
        state.person.method = '';
        state.assumedSettlement = null;
    },
    [mutationTypes.RESET_END_OF_LIFE](state) {
        state.person.ageAtDeath = null;
        state.person.dateOfDeath = null;
        state.results.formula = null;
        state.results.tableData = null;
        state.results.intro = null;
        state.results.explanation = null;
        state.results.outcome = null;
    },
    [mutationTypes.UPDATE_RESULTS](state, data) {
        state.results.intro = data[0].Paragraph.content;
        state.results.explanation = data[1].Paragraph.content;
        state.results.outcome = data[2].Paragraph.content;
        state.results.tableData = data[3].TableData;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/mini/lifeExpectancy.js